// Token support for the customizable email template editors (order
// confirmation and instructions emails).
//
// Each template is persisted as HTML (so rich-text formatting survives), with
// each inserted token rendered as a `{{id}}` placeholder rather than a chip.
// The placeholders are substituted when the mailto body is built; the editor
// converts them back into chips on load.

/** The email kinds that have an editable template. */
export type EmailTemplateType = "order-confirmation" | "instructions";

/** Stable ids used in the `{{id}}` placeholders and as each chip's data. */
export const EMAIL_TEMPLATE_TOKEN_IDS = [
  "pickups",
  "dropoffs",
  "orderReference",
  "cargoType",
  "description",
  "truck",
  "trailer",
  "customer",
  "invoiceRef",
  "invoiceRef2",
] as const;

export type EmailTemplateTokenId = (typeof EMAIL_TEMPLATE_TOKEN_IDS)[number];

/** Base path of the email-templates API per template type. */
export const EMAIL_TEMPLATE_ENDPOINTS: Record<EmailTemplateType, string> = {
  "order-confirmation": "/api/order-confirmation-email-templates",
  instructions: "/api/instructions-email-templates",
};

// Response shape from the email-templates endpoints.
// `data` is jsonb; only `html` is used for now (more fields to come).
export interface EmailTemplateResponse {
  id: string;
  clientId: string | null;
  data: { html?: string };
  lastUpdated: string | null;
}

/**
 * Sample values used to render the live preview. Empty for now — to be filled
 * in with representative example values per token.
 */
export const EMAIL_TEMPLATE_PREVIEW_VALUES: Record<
  EmailTemplateTokenId,
  string
> = {
  pickups: `Johansens Træhandel'
  Helga Pedersens Gade
  DK-8000
  Aarhus
  18.06.2026`,
  dropoffs: `Birkelund ApS
  Banegårdvej 4
  DK-8464
  Galten
  18.06.2026

  Nelis A/S
  Sankt Ols Stræde 3
  DK-8464
  Galten
  18.06.2026`,
  orderReference: "ARNL-492879",
  cargoType: "Diverse",
  description: "3 Paller fra Aarhus til Galten",
  truck: "AY 81 032",
  trailer: "HG 8292",
  customer: "Dora Nordic ApS",
  invoiceRef: "INV-12345",
  invoiceRef2: "INV-67890",
};

/** Matches a `{{tokenId}}` placeholder. Use a fresh regex per scan (stateful g flag). */
export const tokenPlaceholderRegex = () => /\{\{(\w+)\}\}/g;

/**
 * The chip embed's CSS class — shared between the Quill blot definition and the
 * export logic that swaps chips for `{{id}}` placeholders.
 */
export const TOKEN_CLASS = "ql-token";
export const TOKEN_ID_ATTR = "data-token-id";

/** Replace `{{id}}` placeholders in a template string using the given value map. */
export const fillTemplate = (
  template: string,
  values: Record<string, string>
): string =>
  template.replace(
    tokenPlaceholderRegex(),
    (_match, id: string) => values[id] ?? ""
  );

/** Replace `{{id}}` placeholders in a template string with their preview values. */
export const renderPreview = (template: string): string =>
  fillTemplate(template, EMAIL_TEMPLATE_PREVIEW_VALUES);

/**
 * Convert template HTML into plain text suitable for an email body: block
 * elements and `<br>` become line breaks, list items get a bullet, remaining
 * tags are stripped and entities decoded. `{{id}}` placeholders are left intact
 * so they can be filled afterwards.
 */
export const htmlToPlainText = (html: string): string => {
  const withBreaks = html
    .replace(/<li[^>]*>/gi, "• ")
    .replace(/<\/(p|div|h[1-6]|li|tr)>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n");
  const tmp = document.createElement("div");
  tmp.innerHTML = withBreaks;
  const text = tmp.textContent ?? "";
  return text.replace(/\n{3,}/g, "\n\n").trim();
};
